/**
 * 扫码枪检测内核（捕获阶段单例逻辑）。
 *
 * 整合组长方案优势：
 * - 捕获阶段收码，判定扫码后 stopImmediatePropagation，避免 Mousetrap 吞键截断
 * - 以 Enter（可配 Space）为终止锚点
 * - 用 dwell 中位数 / 短占比判枪，不随慢枪字符间隔漂移
 * - 暴露 scanBurstActive，供 ShortcutManager 避让快捷键
 * - 付款严格模式：数字从首键起吞掉，人手在 keyup 后短延迟回放金额（堵 dwell 首拍漏洞）
 *
 * 投递仍由上层 ScannerBridge → ShortcutManager.getScanSink() 完成，业务 onScan 不用改。
 */

const DEFAULT_CONFIG = {
  /** 终止符：Enter；可选同时认 Space */
  suffixes: ['Enter', ' '],
  minLen: 5,
  /** 短 dwell 阈值（ms），区分带中间 */
  maxDwell: 40,
  /** 短 dwell 占比下限 */
  dwellRatio: 0.6,
  /** dwell 中位数上限（ms） */
  medianDwell: 30,
  /** 两次 keydown 间隔超过则重置缓冲 */
  idleReset: 300,
  /**
   * 付款严格模式：人手数字回放前再等这一拍（ms）。
   * 给下一枪键的 keyup 把 scanBurstActive 打成 true 的时间，避免慢机首键误回放。
   */
  humanFlushDelay: 50,
}

export class ScannerCore {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.buffer = ''
    /** 当前是否处于扫码 burst（供快捷键层避让） */
    this.scanBurstActive = false
    /**
     * 付款严格模式：栈顶声明 strictDigitBuffer 时开启。
     * 开启后 0-9 从首个 keydown 起吞掉，杜绝漏进金额快捷键。
     */
    this.strictDigitMode = false
    this._downAt = new Map()
    this._lastDown = 0
    this._dwell = []
    this._onCommit = null
    /** @type {((chars: string) => void) | null} 人手数字回放 */
    this._onHumanFlush = null
    this._humanFlushTimer = null
    this._listening = false
    this._onDown = this._onDown.bind(this)
    this._onUp = this._onUp.bind(this)
  }

  /**
   * 启动捕获阶段监听
   * @param {(code: string) => void} onCommit 确认扫码后的回调
   */
  start(onCommit) {
    if (this._listening) {
      this._onCommit = onCommit
      return
    }
    this._onCommit = onCommit
    window.addEventListener('keydown', this._onDown, true)
    window.addEventListener('keyup', this._onUp, true)
    this._listening = true
  }

  stop() {
    if (!this._listening) return
    window.removeEventListener('keydown', this._onDown, true)
    window.removeEventListener('keyup', this._onUp, true)
    this._listening = false
    this._onCommit = null
    this._reset()
  }

  /**
   * 运行时改配置（门店调参用）
   * @param {object} patch
   */
  setConfig(patch = {}) {
    this.config = { ...this.config, ...patch }
  }

  /**
   * 付款严格模式开关。开启后数字从首键吞掉，人手经 _onHumanFlush 回放。
   * @param {boolean} enabled
   * @param {((chars: string) => void) | null} onHumanFlush
   */
  setStrictDigitMode(enabled, onHumanFlush = null) {
    this.strictDigitMode = !!enabled
    this._onHumanFlush = typeof onHumanFlush === 'function' ? onHumanFlush : null
    if (!this.strictDigitMode) {
      this._cancelHumanFlush()
    }
  }

  getDebugState() {
    return {
      listening: this._listening,
      scanBurstActive: this.scanBurstActive,
      strictDigitMode: this.strictDigitMode,
      bufferLen: this.buffer.length,
      config: { ...this.config },
    }
  }

  _onDown(e) {
    // IME：仅扫码 burst 中硬拦，避免误伤搜索框打中文
    if (this._isIME(e)) {
      if (this.scanBurstActive) {
        e.stopImmediatePropagation()
        e.preventDefault()
      }
      return
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return

    const inEditable = this._isEditableTarget(e)

    // 终止符：Enter / Space
    if (this._isSuffix(e)) {
      this._cancelHumanFlush()
      const code = this.buffer.trim().toUpperCase()
      const { median, shortRatio } = this._dwellStats()
      const isBarcode = code.length >= this.config.minLen && /^[0-9A-Z]+$/.test(code)
      const dwellLikeGun = median <= this.config.medianDwell || shortRatio >= this.config.dwellRatio
      const isScan = isBarcode && dwellLikeGun
      this._reset()
      if (isScan) {
        e.stopImmediatePropagation()
        e.preventDefault()
        if (typeof this._onCommit === 'function') {
          this._onCommit(code)
        }
      }
      // 人手回车/空格：不拦截，交给输入框或快捷键
      return
    }

    // 备注等输入框聚焦：人手数字交给原生输入，不吞键、不进金额回放缓冲
    // 扫码 burst 中仍拦，避免枪码打进备注
    if (inEditable && !this.scanBurstActive) {
      return
    }

    // 付款严格模式：小数点只用于金额，从不进扫码缓冲；立即回放
    if (this.strictDigitMode && e.key === '.') {
      this._cancelHumanFlush()
      e.stopImmediatePropagation()
      e.preventDefault()
      // 若缓冲里还有未回放数字，先冲掉再回放小数点
      if (this.buffer) {
        this._flushHumanNow()
      }
      if (typeof this._onHumanFlush === 'function') {
        this._onHumanFlush('.')
      }
      return
    }

    const ch = this._printable(e)
    if (ch == null) return

    const now = performance.now()
    if (this._lastDown && now - this._lastDown > this.config.idleReset) {
      this._reset()
    }
    this._lastDown = now
    this._downAt.set(e.code || e.key, now)
    this.buffer += ch

    // 后续键到来时取消人手回放，避免把扫码前缀误当金额
    this._cancelHumanFlush()

    const isDigit = /^[0-9]$/.test(ch)
    // 已确认扫码 burst，或付款严格模式下的数字：拦住，防止污染金额 / 触发快捷键
    if (this.scanBurstActive || (this.strictDigitMode && isDigit)) {
      e.stopImmediatePropagation()
      e.preventDefault()
    }
  }

  _onUp(e) {
    const key = e.code || e.key
    const t = this._downAt.get(key)
    if (t == null) return
    this._downAt.delete(key)
    this._dwell.push(performance.now() - t)
    const { median, shortRatio } = this._dwellStats()
    this.scanBurstActive =
      median <= this.config.medianDwell || shortRatio >= this.config.dwellRatio

    if (!this.strictDigitMode) return

    // 已判为枪：保持缓冲，等 Enter 提交，绝不回放金额
    if (this.scanBurstActive) {
      this._cancelHumanFlush()
      return
    }

    // 人手：短延迟后回放（给连按下一数字 / 偶发枪键一拍时间）
    this._cancelHumanFlush()
    this._humanFlushTimer = setTimeout(() => {
      this._humanFlushTimer = null
      if (this.scanBurstActive) return
      this._flushHumanNow()
    }, this.config.humanFlushDelay)
  }

  _flushHumanNow() {
    if (!this.buffer) return
    const buf = this.buffer
    // 只回放数字段；字母留给扫码（付款码多为纯数字，人手金额也只有数字）
    const digits = buf.replace(/[^0-9]/g, '')
    this._reset()
    if (digits && typeof this._onHumanFlush === 'function') {
      this._onHumanFlush(digits)
    }
  }

  _cancelHumanFlush() {
    if (this._humanFlushTimer) {
      clearTimeout(this._humanFlushTimer)
      this._humanFlushTimer = null
    }
  }

  _dwellStats() {
    if (this._dwell.length === 0) return { median: Infinity, shortRatio: 0 }
    const s = [...this._dwell].sort((a, b) => a - b)
    const mid = Math.floor(s.length / 2)
    const median = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
    const shortRatio = this._dwell.filter((d) => d <= this.config.maxDwell).length / this._dwell.length
    return { median, shortRatio }
  }

  _reset() {
    this._cancelHumanFlush()
    this.buffer = ''
    this._dwell = []
    this.scanBurstActive = false
    this._lastDown = 0
    this._downAt.clear()
  }

  _printable(e) {
    return e.key && /^[0-9a-zA-Z]$/.test(e.key) ? e.key : null
  }

  _isSuffix(e) {
    if (e.key === 'Enter') return true
    // Space：仅当缓冲已有内容时才当终止符候选，避免空手按空格被当扫码结束
    if ((e.key === ' ' || e.code === 'Space') && this.config.suffixes.includes(' ')) {
      return this.buffer.length > 0
    }
    return false
  }

  _isEditableTarget(e) {
    const el = e && e.target
    if (!el || !el.tagName) return false
    const tag = el.tagName.toUpperCase()
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.isContentEditable
  }

  _isIME(e) {
    return !!(e && (e.isComposing || e.key === 'Process' || e.keyCode === 229 || e.which === 229))
  }
}

export default ScannerCore
